import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PawPrint, Loader2 } from 'lucide-react';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { use_auth_store } from '@/lib/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const login_schema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export function LoginPage() {
  const navigate = useNavigate();
  const set_session = use_auth_store((s) => s.set_session);
  const [email, set_email] = useState('');
  const [password, set_password] = useState('');
  const [error, set_error] = useState<string | null>(null);
  const [loading, set_loading] = useState(false);

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);
    const parsed = login_schema.safeParse({ email, password });
    if (!parsed.success) {
      set_error('Please enter a valid email and password (min 8 chars).');
      return;
    }
    set_loading(true);
    try {
      const res = await api.post('/admin/auth/login', parsed.data);
      set_session(res.data);
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError) set_error(err.message);
      else set_error('Login failed');
    } finally {
      set_loading(false);
    }
  }

  return (
    <div className="page-gradient flex min-h-svh items-center justify-center p-5">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-button accent-gradient text-white shadow-l1">
            <PawPrint className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
              Pet Market Place
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">Admin Console · Carnival Paws</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Welcome back</CardTitle>
            <CardDescription>Sign in to manage vendors, bookings, and payments.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handle_submit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@petbuddy.in"
                  value={email}
                  onChange={(e) => set_email(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    className="text-xs font-semibold text-primary hover:underline"
                    onClick={() => set_error('Password reset is coming in v1.1')}
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => set_password(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              {error && (
                <div className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} variant="gradient" className="w-full">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          🐾 Internal access only. Need help? Reach the platform team.
        </p>
      </div>
    </div>
  );
}
